import { ReactNode, useEffect, useRef } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { BiDoorOpen, BiVideo } from 'react-icons/bi'
import { FiUser } from 'react-icons/fi'
import { HiOutlineInformationCircle } from 'react-icons/hi'
import { MdOutlineBadge } from 'react-icons/md'

import { Alert, BoxProps, Flex, Icon, Text } from '@chakra-ui/react'

import { Alarm } from '@/components/icons'
import { DeviceType, EventType } from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'

import { EventsT } from '../types/types'
import { getEventName } from '../utils/utils'

interface EventItemIProps {
  event: EventsT
  isCauseOfIncident?: boolean
  shouldShowDeviceInfo?: boolean
}

interface EventDetailItemIProps extends BoxProps {
  label: string
  icon: ReactNode
}

export const EventItem = ({
  event,
  isCauseOfIncident,
  shouldShowDeviceInfo,
}: EventItemIProps) => {
  const {
    detectedAt,
    type,
    cardHolderName,
    badgeNumber,
    deviceName,
    deviceType,
  } = event

  const isTraceLogsPiiEnabled = useFeatureFlag(FeatureFlag.traceLogsPii)
  const isHightlighted = isCauseOfIncident && type !== EventType.DoorUnlocked
  const ref = useRef<HTMLDivElement>(null)
  const hasDeviceInfo = shouldShowDeviceInfo && deviceName
  const shouldShowTailgateAlert =
    isCauseOfIncident && type === EventType.DoorUnlocked

  useEffect(() => {
    if (isHightlighted && ref?.current) {
      ref.current?.scrollIntoView({ behavior: 'auto', block: 'center' })
    }
  }, [isHightlighted])
  return (
    <Flex
      bg={isHightlighted ? 'bg.400' : 'white'}
      borderColor={isHightlighted ? 'border.400' : 'border.200'}
      borderRadius='10px'
      borderWidth='1px'
      color='black'
      data-testId='traceLog_eventItem'
      direction='column'
      px='16px'
      py='8px'
      ref={ref}
    >
      {shouldShowTailgateAlert && <EventItemAlert />}
      <Flex alignItems='center' direction='row' justifyContent='space-between'>
        <Text fontSize='14px' fontWeight='500' maxW='65%' noOfLines={2}>
          {getEventName(event)}
        </Text>
        <Text fontSize='12px' fontWeight='400'>
          {formatDateTime(detectedAt)}
        </Text>
      </Flex>

      <Flex direction='column' fontSize='12px' fontWeight='400' noOfLines={2}>
        {isTraceLogsPiiEnabled && cardHolderName && (
          <EventDetailItem
            icon={<FiUser size='12px' />}
            label={cardHolderName}
            mt='5px'
          />
        )}

        {isTraceLogsPiiEnabled && badgeNumber && (
          <EventDetailItem
            icon={<MdOutlineBadge size='12px' />}
            label={badgeNumber}
          />
        )}
        {hasDeviceInfo && (
          <EventDetailItem
            icon={getEventDeviceIcon(deviceType)}
            label={deviceName}
          />
        )}
      </Flex>
    </Flex>
  )
}

export const EventDetailItem = ({
  label,
  icon,
  ...rest
}: EventDetailItemIProps) => (
  <Flex alignItems='center' direction='row' gap='10px' {...rest}>
    {icon}
    <Text noOfLines={2} width='95%'>
      {label}
    </Text>
  </Flex>
)

export const getEventDeviceIcon = (type: DeviceType) => {
  switch (type) {
    case DeviceType.Alarm:
      return <Icon as={Alarm} boxSize='12px' strokeColor='black' />
    case DeviceType.Camera:
      return <BiVideo color='black' size='12px' />

    default:
      return <BiDoorOpen color='black' size='12px' />
  }
}

export const EventItemAlert = () => (
  <Alert bg='#DBE9FE' borderRadius='10px' mb='10px' p='8px' status='info'>
    <Icon
      as={HiOutlineInformationCircle}
      boxSize='20px'
      color='#1D40B0'
      mr='10px'
    />
    <Text color='#1D40B0' fontSize='12px' fontWeight='300'>
      This Authorized access enabled tailgating
    </Text>
  </Alert>
)
